import React from "react";
//#region types
import { SelectionGroupProps } from "models/propTypes/selectionPropTypes";
import { SelectionKeyType } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Chip, CircularProgress, Grid, Tooltip } from "@mui/material";
import GridR from "../grid/responsiveGrid";
import StyledButton from "../buttons/styledButton";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const SelectionGroup: React.FC<SelectionGroupProps> = (props) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const key = e.currentTarget.getAttribute("data-key");

    // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
    const item = props.items?.find((item) => item.id == key);
    if (item) props.onChange([item]);
  };

  const handleRemove = (id: SelectionKeyType) => {
    props.onChange(props.value.filter((x) => x.id !== id));
  };

  const getValue = () => {
    const value = props.value.map((x, index) => (
      <Grid key={index} container>{`${x.value} ${x.selectedSubText ? `(${x.selectedSubText})` : ""}`}</Grid>
    ));
    return value;
  };

  return (
    <React.Fragment>
      {!props.isReadOnly ? (
        <Grid item container className={clsx(props.className, cssExports.selectionChipsGroup)}>
          <GridR item container className={cssExports.selectionChips} spacing={1} direction="row">
            {props.value.length > 0 && props.hideOthersOnSelection ? (
              props.value.map((x) => (
                <Grid item key={`selected_${x.id}`} className={cssExports.chipContainer}>
                  <Chip
                    className={clsx(cssExports.chip, cssExports.selected, props.disabled ? cssExports.disabled : "")}
                    label={
                      <>
                        {x.value}
                        {x.selectedSubText ? (
                          <label className={cssExports.selectedSubText}>({x.selectedSubText})</label>
                        ) : (
                          ""
                        )}
                      </>
                    }
                    onDelete={
                      props.disabled
                        ? undefined
                        : () => {
                            handleRemove(x.id as SelectionKeyType);
                          }
                    }
                  />
                </Grid>
              ))
            ) : props.items ? (
              props.items.map((item) => {
                const isSelected = props.value.find((x) => x.id === item.id) !== undefined;
                return props.disabled && !isSelected ? (
                  <React.Fragment key={item.id}></React.Fragment>
                ) : (
                  <Tooltip
                    key={item.id}
                    placement="top"
                    title={item.value}
                    classes={{ tooltip: cssExports.tooltip, popper: cssExports.tooltipPopper }}
                    enterDelay={1000}
                    arrow
                  >
                    <Grid item className={cssExports.chipContainer}>
                      <Chip
                        className={clsx(cssExports.chip, isSelected ? cssExports.selected : "")}
                        variant="outlined"
                        data-key={item.id}
                        label={item.value}
                        clickable
                        onClick={!isSelected ? handleClick : undefined}
                        disabled={props.disabled || props.disabledItems?.some((di) => di === item.id)}
                        onDelete={isSelected ? handleRemove.bind(null, item.id as SelectionKeyType) : undefined}
                      />
                    </Grid>
                  </Tooltip>
                );
              })
            ) : (
              <Grid item key={"loading"} className={cssExports.chipContainer}>
                <CircularProgress size={25} className={cssExports.selectionChipLoading} />
              </Grid>
            )}
            {!props.hideViewAll && (props.alwaysDisplayViewAll || (!props.disabled && props.value.length === 0)) ? (
              <Grid item>
                <StyledButton size="xs" onClick={props.onViewAllClick}>
                  {props.viewAllText
                    ? props.viewAllText
                    : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other)}
                </StyledButton>
              </Grid>
            ) : (
              ""
            )}
          </GridR>
        </Grid>
      ) : (
        props.value.length > 0 && getValue()
      )}
    </React.Fragment>
  );
};

export default SelectionGroup;
